import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import { studentApi } from "../../api/services";
import BulkUpload from "../../components/BulkUpload/BulkUpload";
import ConfirmationModal from "../../ui/ConfirmationModal";
import {
  Users as UsersIcon,
  Search,
  Filter,
  UserPlus,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  Upload,
} from "lucide-react";
import Loader from "../../ui/Loader";
import SettingsHeader from "../../ui/SettingsHeader";

const PAGE_SIZE = 20;

const StudentsList = () => {
  const { isAdmin, user } = useAuth();
  const {
    classes: units,
    deleteStudent,
    currentAcademicYear,
    loadStudents,
    loadDashboardStats,
    loadClasses,
    loadParents,
    loading: dataLoading
  } = useData();
  const navigate = useNavigate();

  // Local state for server-side pagination
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1
  });

  const [filters, setFilters] = useState({ search: "", unitId: "all", sectionId: "" });
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const selectedUnitWithSections = React.useMemo(() => {
    if (filters.unitId === "all" || !units.length) return null;
    const unit = units.find(u => u._id === filters.unitId);
    return (unit?.sections?.length > 0) ? unit : null;
  }, [units, filters.unitId]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  // Fetch students from server
  const fetchStudents = useCallback(async () => {
    // 1. Basic checks
    if (!currentAcademicYear) return;

    // 2. Strict Section Check: If unit is selected and it HAS sections, we MUST have a sectionId
    if (selectedUnitWithSections && !filters.sectionId) {
      if (students.length > 0) setStudents([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
      setLoading(false);
      return;
    }

    // 3. Prevent "all" call if it's too heavy or not needed (optional, but good for performance)
    // if (filters.unitId === "all" && !debouncedSearch) { ... }

    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearch,
        unitId: filters.unitId,
        sectionId: filters.sectionId || undefined,
        academicYear: currentAcademicYear,
      };

      const response = await studentApi.getAllStudents(params);
      if (response.success) {
        setStudents(response.data || []);
        if (response.pagination) {
          setPagination(prev => ({ ...prev, ...response.pagination }));
        }
      } else {
        setStudents([]);
      }
    } catch (error) {
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, filters.unitId, filters.sectionId, currentAcademicYear, selectedUnitWithSections, students.length]);

  // Initial load and refetch on filter changes
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Reset page and CLEAR students immediately when unit/section changes to prevent flashing old data
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    // If we've switched to a unit that needs a section, clear now
    if (filters.unitId !== "all") {
       const u = units.find(x => x._id === filters.unitId);
       if (u?.sections?.length > 0 && !filters.sectionId) {
         setStudents([]);
       }
    }
  }, [filters.unitId, filters.sectionId, units]);

  const visibleUnits = units;

  const toggleSelectAll = (checked) => {
    setSelectedIds(checked ? students.map((s) => s._id) : []);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (!selectedIds.length) return;
    
    setDeleting(true);
    try {
      for (const id of selectedIds) {
        await deleteStudent(id);
      }
      setSelectedIds([]);
      setShowDeleteModal(false);
      setSelectedIds([]);
      setShowDeleteModal(false);
      fetchStudents(); // Refresh list
      loadStudents(); // Refresh global context
      loadDashboardStats(); // Refresh stats
    } catch (error) {
    } finally {
      setDeleting(false);
    }
  };

  const handleExportSelected = () => {
    const rows = students.filter((s) => selectedIds.includes(s._id));
    const header = [
      "Student ID",
      "First Name",
      "Last Name",
      "Email",
      "Class",
      "Section",
      "Phone",
    ];
    
    const csvRows = rows.map(s => {
      // Logic for Unit Name
      const unitData = s.enrollment?.unitId || s.unitId;
      let className = unitData?.unitName || '-';
      
      // Logic for Section Name
      const sectionData = s.enrollment?.sectionId || s.sectionId;
      let sectionName = sectionData?.sectionName || sectionData?.name || '-';

      // Fallback to searching in local units context if only IDs are present
      if (className === '-' || sectionName === '-') {
        const uId = unitData?._id || unitData;
        const localUnit = units.find(u => u._id === uId);
        if (localUnit) {
          if (className === '-') className = localUnit.unitName;
          if (sectionName === '-' || sectionName === '') {
            const sId = sectionData?._id || sectionData;
            const localSec = localUnit.sections?.find(sec => sec._id === sId);
            if (localSec) sectionName = localSec.sectionName || localSec.name;
          }
        }
      }

      return [
        s.studentRegistrationId,
        s.firstName,
        s.lastName,
        s.email,
        className,
        sectionName === '-' ? '' : sectionName,
        s.primaryContactNumber || s.phone || s.parentPhone || '-'
      ].map(val => `"${String(val).replace(/"/g, '""')}"`).join(",");
    });

    const csv = [header.join(",")].concat(csvRows).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "students.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {loading && students.length === 0 || dataLoading.classes ? (
        <Loader />
      ) : (
        <>
          <SettingsHeader
            title="Student Management"
            description={null}
            showBack={false}
            icon={UsersIcon}
            buttons={[
              {
                label: "Bulk Upload",
                icon: Upload,
                onClick: () => setShowBulkUpload(true),
                variant: "secondary",
              },
              {
                label: "Add Student",
                icon: UserPlus,
                onClick: () => navigate("/students/new"),
                variant: "default",
              },
            ]}
          >
            <div className="flex-1 flex items-center gap-2 min-w-0 flex-nowrap overflow-x-auto lg:overflow-visible no-scrollbar pb-1">
              {/* Filter Dropdown */}
              <div className="relative min-w-[150px]">
                <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
                <select
                  value={filters.unitId}
                  onChange={(e) => {
                    setFilters((prev) => ({ ...prev, unitId: e.target.value, sectionId: "" }));
                  }}
                  className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none cursor-pointer hover:bg-white"
                >
                  <option value="all">All Classes</option>
                  {visibleUnits.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unitName}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                </div>
              </div>

              {/* Section Selector (Conditional) */}
              {selectedUnitWithSections && (
                <div className="relative min-w-[120px] animate-in fade-in slide-in-from-left-2 duration-300">
                  <Filter className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2 pointer-events-none" />
                  <select
                    value={filters.sectionId}
                    onChange={(e) => setFilters(prev => ({ ...prev, sectionId: e.target.value }))}
                    className="w-full pl-8 pr-7 py-1.5 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all appearance-none cursor-pointer hover:bg-white"
                  >
                    <option value="">Select Section</option>
                    {selectedUnitWithSections.sections.map(sec => (
                      <option key={sec._id} value={sec._id}>{sec.name || sec.sectionName}</option>
                    ))}
                  </select>
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                  </div>
                </div>
              )}

              {/* Search Input */}
              <div className="relative w-full sm:w-56 lg:w-64">
                <Search className="absolute w-3.5 h-3.5 text-gray-400 transform -translate-y-1/2 left-2.5 top-1/2" />
                <input
                  type="text"
                  placeholder="Search students..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full py-1.5 pl-8 pr-3 text-xs bg-gray-50/50 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500/20 focus:border-primary-500 transition-all hover:bg-white"
                />
              </div>
            </div>
          </SettingsHeader>

          <div className="card !p-0 overflow-hidden">
            {students.length === 0 ? (
              <div className="p-8 text-center bg-white">
                {visibleUnits.length === 0 ? (
                  <>
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 15%, white)' }}>
                      <UsersIcon className="w-6 h-6" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                    </div>
                    <h3 className="mb-2 text-base font-bold text-gray-900">
                      {user?.userRole === 'teacher' ? "No Classes Assigned" : "No Classes Found"}
                    </h3>
                    <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                      {user?.userRole === 'teacher' 
                        ? "You haven't been assigned to any classes yet. Please contact your administrator."
                        : "You need to create a class before you can add students."
                      }
                    </p>
                    {isAdmin && (
                      <button
                        onClick={() => navigate("/classes")}
                        className="btn-primary text-sm"
                      >
                        Create your first Class
                      </button>
                    )}
                  </>
                ) : (selectedUnitWithSections && !filters.sectionId) ? (
                  <>
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 rounded-full" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)' }}>
                      <Filter className="w-6 h-6" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                    </div>
                    <h3 className="mb-2 text-base font-bold text-gray-900">
                      Select a Section
                    </h3>
                    <p className="text-sm text-gray-500">
                      Please select a section from the dropdown above to view students.
                    </p>
                  </>
                ) : (
                  <>
                    <div className="inline-flex items-center justify-center w-12 h-12 mb-4 bg-gray-100 rounded-full">
                      <UsersIcon className="w-6 h-6 text-gray-400" />
                    </div>
                    <h3 className="mb-2 text-base font-bold text-gray-900">
                      No students found
                    </h3>
                    <p className="text-sm text-gray-500">
                      Try adjusting your filters or add a new student manually.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-2">
                        <input
                          type="checkbox"
                          onChange={(e) => toggleSelectAll(e.target.checked)}
                          checked={
                            students.length > 0 &&
                            students.every((s) => selectedIds.includes(s._id))
                          }
                        />
                      </th>
                      <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Student
                      </th>
                      <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Registration
                      </th>
                      <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Class / Section
                      </th>
                      <th className="px-3 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                        Contact Phone
                      </th>
                      <th className="px-3 py-2 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {students.map((s) => (
                      <tr key={s._id} className="hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(s._id)}
                            onChange={() => toggleSelect(s._id)}
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900">
                            {s.firstName} {s.lastName}
                          </div>
                          <div className="text-xs text-gray-500">{s.email}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                          {s.studentRegistrationId}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                          {(() => {
                            const unitData = s.enrollment?.unitId || s.unitId;
                            let className = unitData?.unitName;
                            
                            const sectionData = s.enrollment?.sectionId || s.sectionId;
                            let sectionName = sectionData?.sectionName || sectionData?.name;
                            
                            // Fallback to local units context if data is missing
                            if (!className || (!sectionName && sectionData)) {
                              const uId = unitData?._id || unitData;
                              const localUnit = units.find(u => u._id === uId);
                              if (localUnit) {
                                if (!className) className = localUnit.unitName;
                                if (!sectionName) {
                                  const sId = sectionData?._id || sectionData;
                                  const localSec = localUnit.sections?.find(sec => sec._id === sId);
                                  if (localSec) sectionName = localSec.sectionName || localSec.name;
                                }
                              }
                            }
                            
                            return (
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className="font-medium">{className || '-'}</span>
                                {sectionName && (
                                  <span 
                                    className="px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase tracking-tighter"
                                    style={{ 
                                      backgroundColor: 'color-mix(in srgb, var(--primary-color), white 95%)',
                                      color: 'var(--primary-color)',
                                      borderColor: 'color-mix(in srgb, var(--primary-color), white 80%)'
                                    }}
                                  >
                                    {sectionName}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-900 whitespace-nowrap">
                          {(() => {
                            // Try multiple phone number fields in order of preference
                            const phone = s.primaryContactNumber || 
                                        s.phoneNumber || 
                                        s.phone || 
                                        s.parentPhone || 
                                        s.parentId?.phoneNumber || 
                                        s.parentId?.primaryContactNumber ||
                                        s.fatherPhone ||
                                        s.motherPhone;
                            
                            if (!phone) return <span className="text-gray-400">No phone</span>;
                            
                            return (
                              <div className="flex flex-col">
                                <span className="font-medium">{phone}</span>
                                {(s.primaryContactNumber || s.phoneNumber || s.phone) && (
                                  <span className="text-[10px] text-gray-400">Student</span>
                                )}
                                {!(s.primaryContactNumber || s.phoneNumber || s.phone) && phone && (
                                  <span className="text-[10px] text-gray-400">Parent</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-right whitespace-nowrap">
                          <div className="space-x-2">
                            <button
                              onClick={() => navigate(`/students/${s._id}`)}
                              className="text-xs font-bold hover:underline transition-all"
                              style={{ color: 'var(--primary-color)' }}>
                              View
                            </button>
                            <button
                              onClick={() => navigate(`/students/${s._id}/edit`)}
                              className="text-xs font-bold hover:underline transition-all"
                              style={{ color: 'var(--primary-color)' }}
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between lg:flex-nowrap p-4 bg-gray-50 border-t">
              <div className="text-xs font-medium text-gray-500">
                Showing <span className="text-gray-900">{students.length}</span> of <span className="text-gray-900">{pagination.total}</span> students
              </div>
                <div className="flex items-center space-x-3">
                  <select 
                    value={pagination.limit} 
                    onChange={(e) => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}
                    className="p-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-gray-300"
                  >
                    <option value={10}>10 / page</option>
                    <option value={20}>20 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                  </select>
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm">
                  <button
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="px-3 text-xs font-bold text-gray-700 border-x border-gray-100">
                    {pagination.page} / {pagination.pages || 1}
                  </span>
                  <button
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="h-4 w-px bg-gray-200 mx-1" />

                <button
                  disabled={!selectedIds.length}
                  onClick={handleExportSelected}
                  className="flex items-center px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Export
                </button>
                {isAdmin && (
                  <button
                    disabled={!selectedIds.length}
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center px-3 py-1.5 text-xs font-semibold text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Delete Confirmation Modal */}
          <ConfirmationModal
            isOpen={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteSelected}
            title="Delete Students"
            message={`Are you sure you want to delete ${selectedIds.length} student${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone and will remove all associated data.`}
            confirmText={`Delete ${selectedIds.length} Student${selectedIds.length > 1 ? 's' : ''}`}
            cancelText="Cancel"
            type="danger"
            loading={deleting}
          />
          
          {/* Bulk Upload Modal */}
          {showBulkUpload && (
            <BulkUpload
              type="students"
              onUpload={(data) => {
                setShowBulkUpload(false);
                fetchStudents();
                // Refresh global context data
                loadStudents();
                loadClasses();
                loadParents(); // Refresh parents list to show newly created parents
                loadDashboardStats();
              }}
              onCancel={() => setShowBulkUpload(false)}
            />
          )}
          
        </>
      )}
    </div>
  );
};

export default StudentsList;
